import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight, MdArrowBack } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';

interface UserInfo {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
}

interface OfferDetail {
    _id: string;
    offerType: 'marketplace' | 'direct';
    industry?: string;
    subIndustry?: string;
    status?: string;
    sellerId?: UserInfo | null;
    buyerId?: UserInfo | null;
    sellerPrice?: number;
    buyerPrice?: number;
    leadsGenerated?: number;
    weeklyQuantity?: string;
    offerNumber?: string;
    channels?: string[];
    regions?: string[];
    createdAt: string;
    updatedAt?: string;
}

interface Lead {
    _id: string;
    sellerId: { _id: string; firstName: string; lastName: string; email: string } | null;
    buyerId: { _id: string; firstName: string; lastName: string; email: string } | null;
    orderNumber: string;
    status: string;
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'conflict';
    leadrsRate: number;
    customerRate: number;
    comment?: string;
    leadPrice?: number;
    buyerPrice?: number;
    email?: string;
    createdAt: string;
}

export default function BackOfficeOfferDetail() {
    const { offerType, id } = useParams<{ offerType: string; id: string }>();
    const navigate = useNavigate();
    const [offer, setOffer] = useState<OfferDetail | null>(null);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLeads, setTotalLeads] = useState(0);

    useEffect(() => {
        fetchOfferDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, offerType, page]);

    const fetchOfferDetail = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/offers/${offerType}/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page, limit: 50 },
            });

            setOffer(response.data.offer);
            setLeads(response.data.leads);
            setTotalLeads(response.data.totalLeads);
            setTotalPages(response.data.pages);
        } catch (error) {
            console.error('Error fetching offer detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdminReject = async (leadId: string) => {
        const reason = window.prompt('Rejection reason:');
        if (!reason || reason.trim().length === 0) return;

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${CONFIG.apiUrl}/lead/${leadId}/admin-reject`,
                { rejectionReason: reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchOfferDetail();
        } catch (error) {
            console.error('Error rejecting lead:', error);
            alert('Failed to reject lead');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'validated':
            case 'paid':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
            case 'waiting':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'rejected':
            case 'refused':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getApprovalStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'pending':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
            case 'conflict':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 font-bold';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getOfferStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'in-progress':
            case 'running':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'new':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'closed':
            case 'done':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
            default:
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        }
    };

    if (loading && !offer) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-4 text-center">Loading...</div>
                </Card>
            </div>
        );
    }

    if (!offer) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-4 text-center">Offer not found</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px]">
            {/* Back button + Header */}
            <Card extra="p-5 mb-5">
                <div className="mb-4 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/back-office/offers')}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                    >
                        <MdArrowBack /> Back
                    </button>
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Offer {offer.offerNumber || offer._id.slice(-8)}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                offer.offerType === 'marketplace'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                    : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                            }`}>
                                {offer.offerType === 'marketplace' ? 'Marketplace' : 'Direct'}
                            </span>
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getOfferStatusColor(offer.status)}`}>
                                {offer.status || 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Offer info grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Industry</div>
                        <div className="text-sm text-navy-700 dark:text-white">
                            {offer.industry || '-'}
                            {offer.subIndustry && <span className="text-gray-500"> / {offer.subIndustry}</span>}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Prices</div>
                        <div className="text-sm text-navy-700 dark:text-white">
                            Seller: {offer.sellerPrice != null ? `${offer.sellerPrice}` : '-'} | Buyer: {offer.buyerPrice != null ? `${offer.buyerPrice}` : '-'}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Leads Generated</div>
                        <div className="text-sm text-navy-700 dark:text-white">{offer.leadsGenerated ?? 0}</div>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase text-gray-500">Created</div>
                        <div className="text-sm text-navy-700 dark:text-white">
                            {new Date(offer.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </div>
                    </div>
                    {offer.channels && offer.channels.length > 0 && (
                        <div>
                            <div className="text-xs font-bold uppercase text-gray-500">Channels</div>
                            <div className="text-sm text-navy-700 dark:text-white">{offer.channels.join(', ')}</div>
                        </div>
                    )}
                    {offer.regions && offer.regions.length > 0 && (
                        <div>
                            <div className="text-xs font-bold uppercase text-gray-500">Regions</div>
                            <div className="text-sm text-navy-700 dark:text-white">{offer.regions.join(', ')}</div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Seller & Buyer cards */}
            <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                {/* Seller */}
                <Card extra="p-5">
                    <div className="mb-2 text-xs font-bold uppercase text-gray-500">Seller</div>
                    {offer.sellerId ? (
                        <div>
                            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                {offer.sellerId.firstName} {offer.sellerId.lastName}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">{offer.sellerId.email}</div>
                            {offer.sellerId.company && (
                                <div className="text-sm text-gray-500">{offer.sellerId.company}</div>
                            )}
                        </div>
                    ) : (
                        <div className="text-gray-500">No seller assigned</div>
                    )}
                </Card>

                {/* Buyer */}
                <Card extra="p-5">
                    <div className="mb-2 text-xs font-bold uppercase text-gray-500">Buyer</div>
                    {offer.buyerId ? (
                        <div>
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {offer.buyerId.firstName} {offer.buyerId.lastName}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">{offer.buyerId.email}</div>
                            {offer.buyerId.company && (
                                <div className="text-sm text-gray-500">{offer.buyerId.company}</div>
                            )}
                        </div>
                    ) : (
                        <div className="text-gray-500">No buyer assigned</div>
                    )}
                </Card>
            </div>

            {/* Leads table */}
            <Card extra="p-5">
                <div className="mb-4 text-lg font-bold text-navy-700 dark:text-white">
                    Leads ({totalLeads})
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Order #</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Email</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Approval</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Leadrs Rate</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Customer Rate</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Commission</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="py-4 text-center">Loading...</td>
                                </tr>
                            ) : leads.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="py-4 text-center">No leads for this offer</td>
                                </tr>
                            ) : (
                                leads.map((lead) => (
                                    <tr key={lead._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 text-sm font-mono">
                                            {lead.orderNumber || lead._id.slice(-8)}
                                        </td>
                                        <td className="py-3 text-sm">{lead.email || '-'}</td>
                                        <td className="py-3">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(lead.status)}`}>
                                                {lead.status}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            {lead.approvalStatus ? (
                                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getApprovalStatusColor(lead.approvalStatus)}`}>
                                                    {lead.approvalStatus}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="py-3 text-sm">{lead.leadrsRate ? `${lead.leadrsRate}/10` : '-'}</td>
                                        <td className="py-3 text-sm">{lead.customerRate >= 0 ? `${lead.customerRate}/10` : '-'}</td>
                                        <td className="py-3 text-sm font-medium text-green-600 dark:text-green-400">
                                            {lead.buyerPrice && lead.leadPrice ? `${(lead.buyerPrice - lead.leadPrice).toFixed(2)}` : '-'}
                                        </td>
                                        <td className="py-3 text-sm">
                                            {new Date(lead.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="py-3">
                                            {lead.status !== 'rejected' && lead.approvalStatus !== 'rejected' && (
                                                <button
                                                    onClick={() => handleAdminReject(lead._id)}
                                                    className="rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                                                    title="Admin reject this lead"
                                                >
                                                    Reject
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft /> Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                            >
                                Next <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
